import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import KeyDiagramView from "./KeyDiagramView";
import { appendDiagramPagesToPdf, prefetchFontEmbedCSS } from "./diagramPdfCapture";

const AllKeyDiagrams = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const buildingId = searchParams.get("building_id");
  const periodId = searchParams.get("period_id");
  const returnTo = searchParams.get("returnTo");
  const backTarget = returnTo && decodeURIComponent(returnTo).startsWith("/reports") ? decodeURIComponent(returnTo) : "/reports";

  const [keys, setKeys] = useState([]);
  const [diagramDataMap, setDiagramDataMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingDiagrams, setLoadingDiagrams] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [periodName, setPeriodName] = useState("");
  const [periodWhiteLabelling, setPeriodWhiteLabelling] = useState(false);
  const [periodLogoUrl, setPeriodLogoUrl] = useState(null);
  const diagramRefs = useRef({});

  useEffect(() => {
    if (buildingId) {
      fetchKeysAndDiagrams();
      fetchBuildingAndPeriodNames();
    } else {
      setError("Building ID is required. Please go back and apply filters.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, periodId]);

  // Fetch building and period names for display
  const fetchBuildingAndPeriodNames = async () => {
    try {
      if (buildingId) {
        // Fetch building name
        const buildingsRes = await axios.get(`${process.env.REACT_APP_API_URL}building/list`, {
          headers: {
            "api-key": process.env.REACT_APP_API_KEY,
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          }
        });

        if (buildingsRes.data.status === "success") {
          const buildings = buildingsRes.data.data.buildings || [];
          const building = buildings.find(b => b.id === parseInt(buildingId));
          if (building) {
            setBuildingName(building.building_name);
          }
        }

        // Fetch period name if periodId is provided
        if (periodId) {
          const periodsRes = await axios.get(`${process.env.REACT_APP_API_URL}building/${buildingId}/periods`, {
            headers: {
              "api-key": process.env.REACT_APP_API_KEY,
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            }
          });

          if (periodsRes.data.status === "success") {
            const periods = periodsRes.data.data.periods || [];
            const period = periods.find(p => p.id === parseInt(periodId));
            if (period) {
              const start = new Date(period.start_date).toLocaleDateString();
              const end = new Date(period.end_date).toLocaleDateString();
              setPeriodName(`${start} - ${end}`);
              setPeriodWhiteLabelling(period.white_labelling === true);
              setPeriodLogoUrl(period.logo || null);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error fetching building/period names:", err);
    }
  };

  // Fetch keys and their diagram data
  const fetchKeysAndDiagrams = async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch keys based on filters
      const params = { building_id: buildingId };
      if (periodId) params.period_id = periodId;

      const keysRes = await axios.get(`${process.env.REACT_APP_API_URL}reports/keys`, {
        headers: {
          "api-key": process.env.REACT_APP_API_KEY,
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params,
      });

      if (keysRes.data.status === "success") {
        const fetchedKeys = keysRes.data.data || [];
        setKeys(fetchedKeys);

        // Fetch diagram data for each key
        if (fetchedKeys.length > 0) {
          setLoadingDiagrams(true);
          const diagramPromises = fetchedKeys.map(async (key) => {
            try {
              const res = await axios.get(`${process.env.REACT_APP_API_URL}reports/keys/${key.id}`, {
                headers: {
                  "api-key": process.env.REACT_APP_API_KEY,
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              });

              if (res.data.status === "success") {
                return { keyId: key.id, data: res.data.data };
              }
              return null;
            } catch (err) {
              console.error(`Error fetching diagram for key ${key.id}:`, err);
              return null;
            }
          });

          const diagramResults = await Promise.all(diagramPromises);
          const newDiagramDataMap = {};
          diagramResults.forEach(result => {
            if (result) {
              newDiagramDataMap[result.keyId] = result.data;
            }
          });
          setDiagramDataMap(newDiagramDataMap);
        } else {
          setDiagramDataMap({});
        }
      } else {
        setError(keysRes.data.message || "Unable to load keys.");
        setKeys([]);
      }
    } catch (err) {
      console.error("Error fetching keys and diagrams:", err);
      setError(err?.response?.data?.message || err.message || "Unable to load keys and diagrams.");
      setKeys([]);
    } finally {
      setLoading(false);
      setLoadingDiagrams(false);
    }
  };

  // Handle diagram ready callback
  const handleDiagramReady = (keyId, elements) => {
    if (elements?.length) {
      diagramRefs.current[keyId] = elements;
    }
  };

  // Export all diagrams to a single PDF. The per-page React-Flow capture +
  // composite logic now lives in the shared ./diagramPdfCapture module so the
  // Répartition Report export can reuse it identically.
  const handleExportAllPDF = async () => {
    if (keys.length === 0 || Object.keys(diagramDataMap).length === 0) {
      alert("No diagrams to export.");
      return;
    }

    setExporting(true);

    try {
      // Build flat list of all pages
      const allPages = [];
      for (const key of keys) {
        const elements = diagramRefs.current[key.id] || [];
        if (!elements.length) {
          console.warn(`[PDF Export] key ${key.id} has no registered DOM elements — diagram may not have loaded yet`);
        }
        // Each ready entry is { el, nodes } (KeyDiagramView reports nodes so the PDF
        // capture can size from node coords). Tolerate a bare el for safety.
        elements.forEach((entry, pi) =>
          allPages.push({ key, el: entry?.el || entry, nodes: entry?.nodes || [], pi })
        );
      }

      if (allPages.length === 0) {
        alert('No diagram elements found. Please wait for all diagrams to finish loading, then try again.');
        return;
      }

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });

      // Pre-fetch font CSS once, then capture every page into the pdf.
      const fontEmbedCSS = await prefetchFontEmbedCSS(allPages[0].el);
      const pagesAdded = await appendDiagramPagesToPdf({ pdf, pages: allPages, fontEmbedCSS });

      if (pagesAdded > 0) {
        const fileName = `Reports-${buildingName || 'All'}-${periodName || 'All-Periods'}-${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);
      } else {
        alert('No diagrams were captured. Check the browser console for details.');
      }
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("Unable to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-title d-flex align-items-center justify-content-between">
        <div>
          <h1 className="page-section-title mb-1">All Key Diagrams</h1>
          <p className="text-muted mb-0">
            {buildingName && `Building: ${buildingName}`}
            {periodName && ` | Period: ${periodName}`}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => navigate(backTarget)}>
            Back
          </button>
          {keys.length > 0 && Object.keys(diagramDataMap).length > 0 && (
            <button
              className="btn btn-primary"
              onClick={handleExportAllPDF}
              disabled={exporting}
            >
              {exporting ? "Exporting..." : "Export All to PDF"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {loading || loadingDiagrams ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 mb-0">
            {loading ? "Loading keys..." : "Loading diagrams..."}
          </p>
        </div>
      ) : keys.length === 0 ? (
        <div className="text-center py-5">
          <p className="mb-2">No keys found for the selected filters.</p>
          <button className="btn btn-outline-primary" onClick={() => navigate(backTarget)}>
            Go Back to Reports
          </button>
        </div>
      ) : (
        <>
          {exporting && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: 'white',
              }}
            >
              <div className="spinner-border text-light" role="status" style={{ marginBottom: '20px' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>Exporting PDF... Please wait</div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '40px', paddingTop: '20px' }}>
            {keys.map((key) => (
              <div key={key.id} data-diagram-wrapper style={{ marginBottom: '20px' }}>
                {diagramDataMap[key.id] ? (
                  <KeyDiagramView
                    keyId={key.id}
                    reportData={diagramDataMap[key.id]}
                    onDiagramReady={handleDiagramReady}
                    whiteLabellingEnabled={periodWhiteLabelling}
                    customLogoUrl={periodLogoUrl}
                  />
                ) : (
                  <div className="alert alert-warning">
                    Diagram data not available for key: {key.key_name || `#${key.id}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AllKeyDiagrams;
